// Governance Labels Service
// This service handles all API interactions for the governance labeling system

import { api } from '@/api'
import type {
  CreateLabelCategoryRequest,
  UpdateLabelCategoryRequest,
  GovernanceLabel,
  LabelCategory,
  MissionLabel,
  DataSourceLabel,
  ConstraintRule,
  ValidationResult,
  LabelsResponse,
  ConstraintRulesResponse,
  LabelFilters,
  ConstraintRuleFilters,
  CreateLabelRequest,
  UpdateLabelRequest,
  ApplyLabelsRequest,
  CreateConstraintRuleRequest,
  UpdateConstraintRuleRequest,
  ValidateLabelsRequest
} from '@/types/governance-labels'

class GovernanceLabelsService {
  constructor() {
    // Gateway proxies /v1/governance/* to governance-service backend
    // API_CONFIG is used by the unified API client
  }

  // Get relative endpoint for unified API client
  private getRelativeEndpoint(endpoint: string): string {
    // The api client has prefixUrl set to ${GATEWAY_URL}/v1 (https://api.agion.dev/v1)
    // IMPORTANT: Paths must NOT start with a slash when using prefixUrl (Ky requirement)
    //
    // Label endpoints are at /v1/governance/labels (governance service namespace)
    // So we need to add 'governance/' prefix

    // Remove leading slash if present
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint

    // Add governance prefix if not already present
    if (cleanEndpoint.startsWith('governance/')) {
      return cleanEndpoint
    }
    return `governance/${cleanEndpoint}`
  }

  // Label Categories
  async getCategories(): Promise<LabelCategory[]> {
    const data = await api.get<{ categories?: LabelCategory[] } | LabelCategory[]>(this.getRelativeEndpoint('/label-categories'))
    return Array.isArray(data) ? data : (data.categories || [])
  }

  /**
   * The categories a connection's Data rules step asks (agion/Agion#3314):
   * governance-service decides which, so the step never filters them itself.
   * Same shape as getCategories.
   */
  async getDataRulesCategories(): Promise<LabelCategory[]> {
    const data = await api.get<{ categories?: LabelCategory[] } | LabelCategory[]>(this.getRelativeEndpoint('/label-categories'), { for: 'data_rules' })
    return Array.isArray(data) ? data : (data.categories || [])
  }

  /**
   * V200: set a category's scope. Platform-global — server enforces the
   * curator-org gate; non-curator admins receive 403.
   */
  async updateCategoryScope(categoryId: string, scope: 'organization' | 'mission'): Promise<void> {
    await api.put(this.getRelativeEndpoint(`/label-categories/${encodeURIComponent(categoryId)}/scope`), { scope })
  }

  /**
   * Create a platform-global label category (curator org only).
   */
  async createCategory(request: CreateLabelCategoryRequest): Promise<LabelCategory> {
    return api.post<LabelCategory, CreateLabelCategoryRequest>(
      this.getRelativeEndpoint('/label-categories'),
      request
    )
  }

  /**
   * Update a platform-global label category (curator org only).
   */
  async updateCategory(categoryId: string, request: UpdateLabelCategoryRequest): Promise<LabelCategory> {
    return api.put<LabelCategory, UpdateLabelCategoryRequest>(
      this.getRelativeEndpoint(`/label-categories/${encodeURIComponent(categoryId)}`),
      request
    )
  }

  /**
   * Delete a platform-global label category and all of its labels (curator
   * org only). Applied labels cascade away server-side.
   */
  async deleteCategory(categoryId: string): Promise<void> {
    await api.delete<void>(this.getRelativeEndpoint(`/label-categories/${encodeURIComponent(categoryId)}`))
  }

  async getCategoryByName(name: string): Promise<LabelCategory | null> {
    try {
      return await api.get<LabelCategory>(this.getRelativeEndpoint(`/label-categories/${encodeURIComponent(name)}`))
    } catch (error: any) {
      if (error?.status === 404) {
        return null
      }
      throw error
    }
  }


  // Governance Labels
  async getLabels(filters: LabelFilters = {}): Promise<LabelsResponse> {
    const searchParams: Record<string, string> = {}

    if (filters['categoryIds']?.length) {
      searchParams['categoryIds'] = filters['categoryIds'].join(',')
    }
    if (filters['search']) {
      searchParams['search'] = filters['search']
    }
    if (filters['isActive'] !== undefined) {
      searchParams['isActive'] = filters['isActive'].toString()
    }
    if (filters['requiresApproval'] !== undefined) {
      searchParams['requiresApproval'] = filters['requiresApproval'].toString()
    }

    return await api.get<LabelsResponse>(this.getRelativeEndpoint('/labels'), searchParams)
  }

  async getLabelByKey(key: string): Promise<GovernanceLabel | null> {
    try {
      return await api.get<GovernanceLabel>(this.getRelativeEndpoint(`/labels/by-key/${encodeURIComponent(key)}`))
    } catch (error: any) {
      if (error?.status === 404) {
        return null
      }
      throw error
    }
  }

  async getLabelById(id: number): Promise<GovernanceLabel | null> {
    try {
      return await api.get<GovernanceLabel>(this.getRelativeEndpoint(`/labels/${id}`))
    } catch (error: any) {
      if (error?.status === 404) {
        return null
      }
      throw error
    }
  }

  async createLabel(request: CreateLabelRequest): Promise<GovernanceLabel> {
    return await api.post<GovernanceLabel, CreateLabelRequest>(this.getRelativeEndpoint('/labels'), request)
  }

  async updateLabel(id: number | string, request: UpdateLabelRequest): Promise<GovernanceLabel> {
    return await api.put<GovernanceLabel, UpdateLabelRequest>(this.getRelativeEndpoint(`/labels/${id}`), request)
  }

  async deleteLabel(id: number | string): Promise<void> {
    await api.delete<void>(this.getRelativeEndpoint(`/labels/${id}`))
  }

  // Mission Labels
  async getMissionLabels(missionId: string): Promise<MissionLabel[]> {
    const data = await api.get<{ labels?: MissionLabel[] } | MissionLabel[]>(
      this.getRelativeEndpoint(`/missions/${encodeURIComponent(missionId)}/labels`)
    )
    return Array.isArray(data) ? data : (data.labels || [])
  }

  async applyMissionLabels(missionId: string, request: ApplyLabelsRequest): Promise<void> {
    await api.post<void, ApplyLabelsRequest>(
      this.getRelativeEndpoint(`/missions/${encodeURIComponent(missionId)}/labels`),
      request
    )
  }

  async removeMissionLabels(missionId: string): Promise<void> {
    await api.delete<void>(this.getRelativeEndpoint(`/missions/${encodeURIComponent(missionId)}/labels`))
  }

  // Data Source Labels
  async getDataSourceLabels(dataSourceId: string): Promise<DataSourceLabel[]> {
    const data = await api.get<{ labels?: DataSourceLabel[] } | DataSourceLabel[]>(
      this.getRelativeEndpoint(`/data-sources/${encodeURIComponent(dataSourceId)}/labels`)
    )
    return Array.isArray(data) ? data : (data.labels || [])
  }

  async applyDataSourceLabels(
    dataSourceId: string,
    dataSourceType: string,
    request: ApplyLabelsRequest
  ): Promise<void> {
    await api.post<void, ApplyLabelsRequest & { dataSourceType: string }>(
      this.getRelativeEndpoint(`/data-sources/${encodeURIComponent(dataSourceId)}/labels`),
      {
        ...request,
        dataSourceType
      }
    )
  }

  async removeDataSourceLabels(dataSourceId: string): Promise<void> {
    await api.delete<void>(
      this.getRelativeEndpoint(`/data-sources/${encodeURIComponent(dataSourceId)}/labels`)
    )
  }

  // Constraint Rules
  async getConstraintRules(filters: ConstraintRuleFilters = {}): Promise<ConstraintRulesResponse> {
    const searchParams: Record<string, string> = {}

    if (filters['ruleTypes']?.length) {
      searchParams['ruleTypes'] = filters['ruleTypes'].join(',')
    }
    if (filters['isActive'] !== undefined) {
      searchParams['isActive'] = filters['isActive'].toString()
    }
    if (filters['minPriority'] !== undefined) {
      searchParams['minPriority'] = filters['minPriority'].toString()
    }
    if (filters['maxPriority'] !== undefined) {
      searchParams['maxPriority'] = filters['maxPriority'].toString()
    }
    if (filters['search']) {
      searchParams['search'] = filters['search']
    }

    return await api.get<ConstraintRulesResponse>(
      this.getRelativeEndpoint('/constraint-rules'),
      searchParams
    )
  }

  async getConstraintRule(id: number): Promise<ConstraintRule | null> {
    try {
      return await api.get<ConstraintRule>(this.getRelativeEndpoint(`/constraint-rules/${id}`))
    } catch (error: any) {
      if (error?.status === 404) {
        return null
      }
      throw error
    }
  }

  async createConstraintRule(request: CreateConstraintRuleRequest): Promise<ConstraintRule> {
    return await api.post<ConstraintRule, CreateConstraintRuleRequest>(
      this.getRelativeEndpoint('/constraint-rules'),
      request
    )
  }

  async updateConstraintRule(id: number, request: UpdateConstraintRuleRequest): Promise<ConstraintRule> {
    return await api.put<ConstraintRule, UpdateConstraintRuleRequest>(
      this.getRelativeEndpoint(`/constraint-rules/${id}`),
      request
    )
  }

  async deleteConstraintRule(id: number): Promise<void> {
    await api.delete<void>(this.getRelativeEndpoint(`/constraint-rules/${id}`))
  }

  async toggleConstraintRule(id: number, isActive: boolean): Promise<void> {
    await api.patch<void, { isActive: boolean }>(
      this.getRelativeEndpoint(`/constraint-rules/${id}/toggle`),
      { isActive }
    )
  }

  // Validation
  async validateLabels(request: ValidateLabelsRequest): Promise<ValidationResult> {
    return await api.post<ValidationResult, ValidateLabelsRequest>(
      this.getRelativeEndpoint('/validate'),
      request
    )
  }

  async validateMissionLabels(missionId: string, labelIds: number[]): Promise<ValidationResult> {
    return this.validateLabels({
      entityType: 'mission',
      entityId: missionId,
      labelIds
    })
  }

  async validateDataSourceLabels(dataSourceId: string, labelIds: number[]): Promise<ValidationResult> {
    return this.validateLabels({
      entityType: 'data_source',
      entityId: dataSourceId,
      labelIds
    })
  }

  // Batch Operations
  async batchApplyLabelsToMissions(missionIds: string[], labelIds: number[], notes?: string): Promise<void> {
    await api.post<void, { missionIds: string[]; labelIds: number[]; notes?: string }>(
      this.getRelativeEndpoint('/missions/batch-apply-labels'),
      {
        missionIds,
        labelIds,
        notes
      }
    )
  }

  async batchRemoveLabelsFromMissions(missionIds: string[], labelIds: number[]): Promise<void> {
    await api.post<void, { missionIds: string[]; labelIds: number[] }>(
      this.getRelativeEndpoint('/missions/batch-remove-labels'),
      {
        missionIds,
        labelIds
      }
    )
  }

  // Analytics and Reporting
  async getLabelUsageStats(): Promise<{
    totalLabels: number
    totalLabeledEntities: number
    labelDistribution: Array<{
      label: GovernanceLabel
      count: number
      percentage: number
    }>
    categoryDistribution: Array<{
      category: LabelCategory
      count: number
      percentage: number
    }>
  }> {
    return await api.get<{
      totalLabels: number
      totalLabeledEntities: number
      labelDistribution: Array<{
        label: GovernanceLabel
        count: number
        percentage: number
      }>
      categoryDistribution: Array<{
        category: LabelCategory
        count: number
        percentage: number
      }>
    }>(this.getRelativeEndpoint('/analytics/usage'))
  }

  async getComplianceReport(): Promise<{
    totalViolations: number
    totalWarnings: number
    violationsByRule: Array<{
      rule: ConstraintRule
      count: number
    }>
    recentActivity: Array<{
      timestamp: string
      type: 'violation' | 'warning' | 'resolution'
      entity: { type: string; id: string; name: string }
      rule: string
      message: string
    }>
  }> {
    return await api.get<{
      totalViolations: number
      totalWarnings: number
      violationsByRule: Array<{
        rule: ConstraintRule
        count: number
      }>
      recentActivity: Array<{
        timestamp: string
        type: 'violation' | 'warning' | 'resolution'
        entity: { type: string; id: string; name: string }
        rule: string
        message: string
      }>
    }>(this.getRelativeEndpoint('/analytics/compliance'))
  }

  // Search and Suggestions
  async searchLabels(query: string, limit: number = 10): Promise<GovernanceLabel[]> {
    // Use the standard /labels endpoint with search parameter
    // Backend ListLabels supports: ?search=query&limit=N
    const data = await api.get<{ labels?: GovernanceLabel[] } | GovernanceLabel[]>(
      this.getRelativeEndpoint('/labels'),
      { search: query, limit: limit.toString() }
    )
    return Array.isArray(data) ? data : (data.labels || [])
  }

  async getSuggestedLabels(entityType: string, entityId: string): Promise<{
    suggested: GovernanceLabel[]
    reasons: Array<{
      label: GovernanceLabel
      reason: string
      confidence: number
    }>
  }> {
    return await api.get<{
      suggested: GovernanceLabel[]
      reasons: Array<{
        label: GovernanceLabel
        reason: string
        confidence: number
      }>
    }>(this.getRelativeEndpoint('/suggestions'), {
      entityType,
      entityId
    })
  }
}

// Export singleton instance
export const governanceLabelsService = new GovernanceLabelsService()
export default governanceLabelsService
